import type { InventoryApiRow, InventoryVariantUpdate } from '../../_services/inventory.service';
import { pricePerM2 } from '../products/price-per-m2';

// Turning an edited Inventory export back into updates.
//
// Rows are matched on the ID column (the variant id the export writes).
// Only these columns are applied, through the same save as editing an item
// by hand (validation, stock history, cost-price permission):
//   Kettering, Markstey     - stock, whole numbers, 0 or more; blank = leave as is
//   Regular Price           - more than zero; blank = leave as is
//   Discounted Price        - below the regular price; blank or 0 = not on sale
//   Cost Price              - 0 or more; blank = not recorded (only for people who can see cost)
// Everything else in the sheet (name, SKU, category, coverage, barcode...) is
// left alone. When a price changes, the price per m² follows it - worked
// out by the server on save, and shown here with the same rule. A row with any problem is skipped whole, never half-applied.

export type ImportField = 'kStockQty' | 'mStockQty' | 'price' | 'discPrice' | 'costPrice' | 'priceMS';

export interface ImportChange {
  field: ImportField;
  label: string;
  from: number | null;
  to: number | null;
  money: boolean;
  // Worked out from the new price, not read from the sheet.
  auto?: boolean;
}

export interface ImportUpdate {
  id: number;
  name: string;
  variant: string;
  sku: string;
  update: InventoryVariantUpdate;
  changes: ImportChange[];
}

export interface ImportProblem {
  row: number; // the spreadsheet row number, as Excel shows it
  name: string;
  message: string;
}

export interface ImportPlan {
  updates: ImportUpdate[];
  unchanged: number;
  problems: ImportProblem[];
  // Columns that will be applied / that were found but are not imported.
  applied: string[];
  ignored: string[];
  rowsRead: number;
}

const COLUMNS: { field: ImportField; header: string; money: boolean }[] = [
  { field: 'kStockQty', header: 'Kettering', money: false },
  { field: 'mStockQty', header: 'Markstey', money: false },
  { field: 'price', header: 'Regular Price', money: true },
  { field: 'discPrice', header: 'Discounted Price', money: true },
  { field: 'costPrice', header: 'Cost Price', money: true },
];

const blank = (v: unknown) => v === undefined || v === null || String(v).trim() === '';

// "£1,234.50", "1234.5", 12 -> 1234.5; blank -> undefined; nonsense -> NaN.
export function readNumber(v: unknown): number | undefined {
  if (blank(v)) return undefined;
  if (typeof v === 'number') return v;
  const s = String(v).trim().replace(/[£,\s]/g, '');
  return s === '' ? undefined : Number(s);
}

const money = (n: number) => Math.round(n * 100) / 100;
const same = (a: number | null, b: number | null, isMoney: boolean) =>
  a === null || b === null ? a === b : isMoney ? Math.abs(a - b) < 0.005 : a === b;

export function planImport(
  sheetRows: Record<string, unknown>[],
  current: Map<number, InventoryApiRow>,
  canSeeCost: boolean,
): ImportPlan {
  // Header names as written in the sheet, matched without case or extra spaces.
  const headers = new Map<string, string>();
  for (const row of sheetRows.slice(0, 50)) for (const k of Object.keys(row)) headers.set(k.trim().toLowerCase(), k);
  const col = (name: string) => headers.get(name.toLowerCase());
  const idCol = col('ID');
  const skuCol = col('SKU');
  const nameCol = col('Name');
  const usable = COLUMNS.filter((c) => col(c.header) && (c.field !== 'costPrice' || canSeeCost));
  const plan: ImportPlan = {
    updates: [],
    unchanged: 0,
    problems: [],
    applied: usable.map((c) => c.header),
    ignored: [...headers.values()].filter((h) => !['id', ...usable.map((c) => c.header.toLowerCase())].includes(h.trim().toLowerCase())),
    rowsRead: sheetRows.length,
  };
  if (!idCol) {
    plan.problems.push({ row: 1, name: '', message: 'No "ID" column - use a sheet downloaded with Export, keeping the ID column.' });
    return plan;
  }
  if (!usable.length) {
    plan.problems.push({ row: 1, name: '', message: 'No columns to import - expected Kettering, Markstey, Regular Price, Discounted Price or Cost Price.' });
    return plan;
  }

  const seen = new Set<number>();
  sheetRows.forEach((sheetRow, i) => {
    const rowNo = i + 2; // row 1 is the header
    const label = String((nameCol && sheetRow[nameCol]) ?? '').trim();
    const rawId = sheetRow[idCol];
    if (blank(rawId) && usable.every((c) => blank(sheetRow[col(c.header)!]))) return; // empty line
    const id = readNumber(rawId);
    if (id === undefined || !Number.isInteger(id)) {
      plan.problems.push({ row: rowNo, name: label, message: 'ID is missing or not a number' });
      return;
    }
    if (seen.has(id)) {
      plan.problems.push({ row: rowNo, name: label, message: `ID ${id} appears more than once - only the first is used` });
      return;
    }
    seen.add(id);
    const cur = current.get(id);
    if (!cur) {
      plan.problems.push({ row: rowNo, name: label, message: `No item with ID ${id}` });
      return;
    }
    const name = cur.productName;
    const sheetSku = skuCol ? String(sheetRow[skuCol] ?? '').trim() : '';
    if (sheetSku && cur.sku && sheetSku.toLowerCase() !== String(cur.sku).trim().toLowerCase()) {
      plan.problems.push({ row: rowNo, name, message: `SKU "${sheetSku}" doesn't match ID ${id} (${cur.sku}) - check the row wasn't moved` });
      return;
    }

    const rowProblems: string[] = [];
    const next: Partial<Record<ImportField, number | null>> = {};
    for (const c of usable) {
      const v = readNumber(sheetRow[col(c.header)!]);
      if (c.field === 'kStockQty' || c.field === 'mStockQty') {
        if (v === undefined) continue; // blank: leave as is
        if (!Number.isInteger(v) || v < 0) rowProblems.push(`${c.header} must be a whole number, 0 or more`);
        else next[c.field] = v;
      } else if (c.field === 'price') {
        if (v === undefined) continue;
        if (!(v > 0)) rowProblems.push('Regular Price must be more than £0');
        else next.price = money(v);
      } else if (c.field === 'discPrice') {
        if (v !== undefined && (Number.isNaN(v) || v < 0)) rowProblems.push('Discounted Price must be a price, or blank for no discount');
        else next.discPrice = v === undefined || v === 0 ? null : money(v);
      } else if (c.field === 'costPrice') {
        if (v !== undefined && (Number.isNaN(v) || v < 0)) rowProblems.push('Cost Price must be 0 or more, or blank');
        else next.costPrice = v === undefined ? null : money(v);
      }
    }
    const price = next.price ?? Number(cur.price);
    const disc = next.discPrice !== undefined ? next.discPrice : cur.discPrice;
    if (!rowProblems.length && disc !== null && disc !== undefined && Number(disc) >= price) {
      rowProblems.push(`Discounted Price (£${Number(disc).toFixed(2)}) must be lower than the Regular Price (£${price.toFixed(2)})`);
    }
    if (rowProblems.length) {
      plan.problems.push({ row: rowNo, name, message: rowProblems.join('; ') });
      return;
    }

    const was: Record<Exclude<ImportField, 'priceMS'>, number | null> = {
      kStockQty: Number(cur.kStockQty) || 0,
      mStockQty: Number(cur.mStockQty) || 0,
      price: Number(cur.price),
      discPrice: cur.discPrice === null || cur.discPrice === undefined ? null : Number(cur.discPrice),
      costPrice: cur.costPrice === null || cur.costPrice === undefined ? null : Number(cur.costPrice),
    };
    const changes: ImportChange[] = [];
    const update: InventoryVariantUpdate = {};
    for (const c of usable) {
      if (!(c.field in next)) continue;
      const f = c.field as Exclude<ImportField, 'priceMS'>;
      const to = next[f] as number | null;
      if (same(was[f], to, c.money)) continue;
      changes.push({ field: f, label: c.header, from: was[f], to, money: c.money });
      (update as Record<string, number | null>)[f] = to;
    }
    // A new selling price brings a new price per m² (saved by the server).
    if ('price' in update || 'discPrice' in update) {
      const ms = pricePerM2(
        { price: cur.price, discPrice: cur.discPrice, approxCoverage: cur.approxCoverage, priceMS: cur.priceMS },
        { price: update.price ?? cur.price, discPrice: 'discPrice' in update ? update.discPrice : cur.discPrice, approxCoverage: cur.approxCoverage },
      );
      const wasMS = Number(cur.priceMS) > 0 ? Number(cur.priceMS) : null;
      if (ms !== null && !same(wasMS, ms, true)) {
        changes.push({ field: 'priceMS', label: 'Price /m²', from: wasMS, to: ms, money: true, auto: true });
      }
    }
    if (!changes.length) {
      plan.unchanged++;
      return;
    }
    plan.updates.push({ id, name, variant: cur.label || '', sku: cur.sku, update, changes });
  });
  return plan;
}
